// AdminWeeklyQuestions.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, Download, Upload, Plus, Edit2, Trash2, 
  Save, X, ChevronDown, ChevronRight, GripVertical,
  AlertCircle, CheckCircle, Image as ImageIcon
} from 'lucide-react';
import api from '../api';

const AdminWeeklyQuestions = () => {
  const [bands, setBands] = useState([]);
  const [questionSets, setQuestionSets] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestion, setNewQuestion] = useState(null);
  const [expandedBands, setExpandedBands] = useState({});
  const [expandedSets, setExpandedSets] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [bandsRes, setsRes, questionsRes] = await Promise.all([
        api.get('/children/global-bands/'),
        api.get('/children/global-question-sets/'),
        api.get('/children/weekly-checklist-questions/')
      ]);
      setBands(bandsRes.data);
      setQuestionSets(setsRes.data);
      setQuestions(questionsRes.data);
      
      const bandsWithQuestions = {};
      const setsWithQuestions = {};
      bandsRes.data.forEach(band => {
        const bandSets = setsRes.data.filter(s => s.band === band.id);
        bandSets.forEach(set => {
          if (questionsRes.data.some(q => q.band_set === set.id)) {
            setsWithQuestions[set.id] = true;
            bandsWithQuestions[band.id] = true;
          }
        });
      });
      setExpandedBands(bandsWithQuestions);
      setExpandedSets(setsWithQuestions);
    } catch (error) {
      showNotification('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      const response = await api.get('/children/weekly-checklist-questions/export/');
      const exportData = {
        version: '1.0',
        exportDate: new Date().toISOString(),
        bands: response.data
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `weekly-questions-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showNotification('Questions exported successfully');
    } catch (error) {
      showNotification('Error exporting questions', 'error');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/children/weekly-checklist-questions/import/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      showNotification('Questions imported successfully. Refreshing data...');
      fetchData(); // Refresh data after import
    } catch (error) {
      showNotification(`Error importing file. ${error.message}`, 'error');
    }
    
    event.target.value = '';
  };

  const handleClearAll = async () => {
    if (!window.confirm('Are you sure you want to delete all weekly checklist questions? This action cannot be undone.')) return;

    try {
      await api.post('/children/weekly-checklist-questions/clear_all/');
      showNotification('All questions have been deleted. Refreshing data...');
      fetchData(); // Refresh data after clearing all
    } catch (error) {
      showNotification('Error clearing questions', 'error');
    }
  };

  const handleAddQuestion = (bandSetId) => {
    setNewQuestion({
      band_set: bandSetId,
      text: '',
      order: questions.filter(q => q.band_set === bandSetId).length,
      is_active: true,
      image: null,
    });
    setEditingQuestion(null);
  };

  const handleSaveNewQuestion = async () => {
    if (!newQuestion.text.trim()) {
      showNotification('Please enter question text', 'error');
      return;
    }
    const formData = new FormData();
    formData.append('band_set', newQuestion.band_set);
    formData.append('text', newQuestion.text);
    formData.append('order', newQuestion.order);
    formData.append('is_active', newQuestion.is_active);
    if (newQuestion.image) {
      formData.append('image_upload', newQuestion.image);
    }

    try {
      const response = await api.post('/children/weekly-checklist-questions/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setQuestions(prev => [...prev, response.data]);
      setNewQuestion(null);
      showNotification('Question added successfully');
    } catch (error) {
      showNotification('Error adding question', 'error');
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion({ ...question });
    setNewQuestion(null);
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion.text.trim()) {
      showNotification('Please enter question text', 'error');
      return;
    }
    const formData = new FormData();
    formData.append('text', editingQuestion.text);
    formData.append('is_active', editingQuestion.is_active);

    if (editingQuestion.image instanceof File) {
      formData.append('image_upload', editingQuestion.image);
    } else if (editingQuestion.image === null) {
      formData.append('image_upload', '');
    }

    try {
      const response = await api.patch(`/children/weekly-checklist-questions/${editingQuestion.id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setQuestions(prev => prev.map(q => 
        q.id === editingQuestion.id ? response.data : q
      ));
      setEditingQuestion(null);
      showNotification('Question updated successfully');
    } catch (error) {
      showNotification('Error updating question', 'error');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await api.delete(`/children/weekly-checklist-questions/${questionId}/`);
      setQuestions(prev => prev.filter(q => q.id !== questionId));
      showNotification('Question deleted successfully');
    } catch (error) {
      showNotification('Error deleting question', 'error');
    }
  };

  const handleDragStart = (e, question, setId) => {
    setDraggedItem({ question, setId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetQuestion, targetSetId) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.question.id === targetQuestion.id || draggedItem.setId !== targetSetId) {
      setDraggedItem(null);
      return;
    }

    const setQuestionsList = questions
      .filter(q => q.band_set === targetSetId)
      .sort((a, b) => a.order - b.order);

    const draggedIndex = setQuestionsList.findIndex(q => q.id === draggedItem.question.id);
    const targetIndex = setQuestionsList.findIndex(q => q.id === targetQuestion.id);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      const reordered = [...setQuestionsList];
      const [removed] = reordered.splice(draggedIndex, 1);
      reordered.splice(targetIndex, 0, removed);

      const updates = reordered.map((q, index) => ({
        id: q.id,
        order: index
      }));

      try {
        await api.post('/children/weekly-checklist-questions/reorder/', { updates });
        
        setQuestions(prev => prev.map(q => {
          const update = updates.find(u => u.id === q.id);
          return update ? { ...q, order: update.order } : q;
        }));
        
        showNotification('Questions reordered successfully');
      } catch (error) {
        showNotification('Error reordering questions', 'error');
      }
    }
    
    setDraggedItem(null);
  };

  const toggleBandExpansion = (bandId) => {
    setExpandedBands(prev => ({ ...prev, [bandId]: !prev[bandId] }));
  };

  const toggleSetExpansion = (setId) => {
    setExpandedSets(prev => ({ ...prev, [setId]: !prev[setId] }));
  };

  const getSetQuestions = (bandSetId) => {
    return questions
      .filter(q => q.band_set === bandSetId)
      .sort((a, b) => a.order - b.order);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-[#1e3a5f]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-2">Weekly Checklist Questions</h1>
          <p className="text-gray-600">Manage weekly developmental checklist questions for each age band</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Total Bands</p>
              <p className="text-3xl font-bold text-[#1e3a5f]">{bands.length}</p>
            </div>
            <div className="bg-blue-500 p-4 rounded-xl">
              <Calendar className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-purple-50 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Total Questions</p>
              <p className="text-3xl font-bold text-[#1e3a5f]">{questions.length}</p>
            </div>
            <div className="bg-purple-500 p-4 rounded-xl">
              <AlertCircle className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-green-50 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Active Questions</p>
              <p className="text-3xl font-bold text-[#1e3a5f]">
                {questions.filter(q => q.is_active).length}
              </p>
            </div>
            <div className="bg-green-500 p-4 rounded-xl">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-orange-50 rounded-2xl p-6 shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Avg per Band</p>
              <p className="text-3xl font-bold text-[#1e3a5f]">
                {bands.length ? Math.round(questions.length / bands.length) : 0}
              </p>
            </div>
            <div className="bg-orange-500 p-4 rounded-xl">
              <ImageIcon className="w-8 h-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Download className="w-4 h-4" />
          Export Questions
        </button>
        <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
          <Upload className="w-4 h-4" />
          Import Questions
          <input
            type="file"
            accept=".json"
            onChange={handleImport}
            className="hidden"
          />
        </label>
        <button
          onClick={handleClearAll}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Clear All Questions
        </button>
      </div>

      {/* Questions by Band and Set */}
      <div className="space-y-4">
        {bands.map(band => {
          const bandSets = questionSets.filter(s => s.band === band.id).sort((a, b) => a.order - b.order);
          const isBandExpanded = expandedBands[band.id];

          return (
            <div key={band.id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div
                className="bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] p-6 cursor-pointer"
                onClick={() => toggleBandExpansion(band.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {isBandExpanded ? <ChevronDown className="w-6 h-6 text-white" /> : <ChevronRight className="w-6 h-6 text-white" />}
                    <div>
                      <h2 className="text-2xl font-bold text-white">{band.title}</h2>
                      <p className="text-white/80 text-sm mt-1">{band.age_min_months}-{band.age_max_months} months</p>
                    </div>
                  </div>
                </div>
              </div>

              {isBandExpanded && (
                <div className="p-4 space-y-3">
                  {bandSets.length > 0 ? bandSets.map(set => {
                    const setQuestions = getSetQuestions(set.id);
                    const isSetExpanded = expandedSets[set.id];

                    return (
                      <div key={set.id} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                        <div
                          className="bg-gray-100 p-4 cursor-pointer hover:bg-gray-200 transition-colors"
                          onClick={() => toggleSetExpansion(set.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {isSetExpanded ? <ChevronDown className="w-5 h-5 text-gray-600" /> : <ChevronRight className="w-5 h-5 text-gray-600" />}
                              <div>
                                <h3 className="font-bold text-gray-800">{set.title}</h3>
                                <p className="text-gray-600 text-sm">{setQuestions.length} questions</p>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddQuestion(set.id);
                                if (!isSetExpanded) toggleSetExpansion(set.id);
                              }}
                              className="bg-white hover:bg-gray-50 text-gray-600 p-2 rounded-lg transition-colors border border-gray-300"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {isSetExpanded && (
                          <div className="p-4">
                            {newQuestion && newQuestion.band_set === set.id && (
                              <div className="mb-4 p-4 bg-green-50 rounded-xl border-2 border-green-200">
                                <div className="flex items-start gap-3">
                                  <GripVertical className="w-5 h-5 text-gray-400 mt-2" />
                                  <div className="flex-1">
                                    <textarea
                                      value={newQuestion.text}
                                      onChange={(e) => setNewQuestion({ ...newQuestion, text: e.target.value })}
                                      placeholder="Enter question text..."
                                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[80px]"
                                      autoFocus
                                    />
                                    <div className="mt-2">
                                      <label className="block text-sm font-medium text-gray-700">Image</label>
                                      <input
                                        type="file"
                                        onChange={(e) => setNewQuestion({ ...newQuestion, image: e.target.files[0] })}
                                        className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                                      />
                                      {newQuestion.image && (
                                        <div className="mt-2">
                                          <img src={URL.createObjectURL(newQuestion.image)} alt="Preview" className="h-16 w-16 object-cover rounded-md" />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={handleSaveNewQuestion}
                                      className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                      <Save className="w-5 h-5" />
                                    </button>
                                    <button
                                      onClick={() => setNewQuestion(null)}
                                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                    >
                                      <X className="w-5 h-5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {setQuestions.length > 0 ? (
                              <div className="space-y-3">
                                {setQuestions.map((question, index) => (
                                  <div
                                    key={question.id}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, question, set.id)}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, question, set.id)}
                                    className={`p-4 bg-white rounded-xl border-2 border-gray-200 hover:shadow-md transition-all cursor-move ${
                                      !question.is_active ? 'opacity-50' : ''
                                    }`}
                                  >
                                    {editingQuestion?.id === question.id ? (
                                      <div className="flex items-start gap-3">
                                        <GripVertical className="w-5 h-5 text-gray-400 mt-2" />
                                        <div className="flex-1">
                                          <textarea
                                            value={editingQuestion.text}
                                            onChange={(e) => setEditingQuestion({ ...editingQuestion, text: e.target.value })}
                                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                                            autoFocus
                                          />
                                          <div className="mt-2">
                                            <label className="block text-sm font-medium text-gray-700">Image</label>
                                            <input
                                              type="file"
                                              onChange={(e) => setEditingQuestion({ ...editingQuestion, image: e.target.files[0] })}
                                              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                            />
                                            {editingQuestion.image && (
                                              <div className="mt-2 flex items-center gap-2">
                                                <img 
                                                  src={editingQuestion.image instanceof File ? URL.createObjectURL(editingQuestion.image) : editingQuestion.image} 
                                                  alt="Preview" 
                                                  className="h-16 w-16 object-cover rounded-md" 
                                                />
                                                <button onClick={() => setEditingQuestion({...editingQuestion, image: null})} className="text-red-500 hover:text-red-700">
                                                  <Trash2 size={16} />
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-3 mt-3">
                                            <label className="flex items-center gap-2">
                                              <input
                                                type="checkbox"
                                                checked={editingQuestion.is_active}
                                                onChange={(e) => setEditingQuestion({ ...editingQuestion, is_active: e.target.checked })}
                                                className="w-4 h-4"
                                              />
                                              <span className="text-sm text-gray-600">Active</span>
                                            </label>
                                          </div>
                                        </div>
                                        <div className="flex gap-2">
                                          <button
                                            onClick={handleUpdateQuestion}
                                            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                          >
                                            <Save className="w-5 h-5" />
                                          </button>
                                          <button
                                            onClick={() => setEditingQuestion(null)}
                                            className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                                          >
                                            <X className="w-5 h-5" />
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-start gap-3">
                                        <GripVertical className="w-5 h-5 text-gray-400 mt-1" />
                                        {question.image && (
                                          <img src={question.image} alt="Question" className="h-16 w-16 object-cover rounded-md flex-shrink-0" />
                                        )}
                                        <div className="flex-1">
                                          <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                              <span className="inline-block px-2 py-1 bg-gray-200 text-gray-700 text-xs font-semibold rounded-full mb-2">
                                                Question {index + 1}
                                              </span>
                                              <p className="text-gray-800">{question.text}</p>
                                              {!question.is_active && (
                                                <span className="inline-block mt-2 px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
                                                  Inactive
                                                </span>
                                              )}
                                            </div>
                                            <div className="flex gap-2 ml-4">
                                              <button
                                                onClick={() => handleEditQuestion(question)}
                                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                              >
                                                <Edit2 className="w-4 h-4" />
                                              </button>
                                              <button
                                                onClick={() => handleDeleteQuestion(question.id)}
                                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                No questions added for this set yet.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }) : (
                    <div className="text-center py-8 text-gray-500">
                      No question sets for this age band yet.
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Notification */}
      {notification && (
        <div className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg transition-all transform ${
          notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white`}>
          <div className="flex items-center gap-2">
            {notification.type === 'error' ? (
              <AlertCircle className="w-5 h-5" />
            ) : (
              <CheckCircle className="w-5 h-5" />
            )}
            <span>{notification.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWeeklyQuestions;
